import React, { useState, useEffect, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";

import api from "../Utils/api";
import session from "../Utils/session";
import logo from "../Assets/logo.png";
import Button from "../Components/Button";
import { usePlausible } from "../hooks/usePlausible";

export default function SignUp() {
  const navigate = useNavigate();
  const { code } = useParams();
  const { trackEvent } = usePlausible();
  const [inviteOnly, setInviteOnly] = useState(true);
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [cPassword, setCPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [inviteCode, setInviteCode] = useState(null);
  const [useInviteCode, setUseInviteCode] = useState(false);
  const [inviteCodeData, setInviteCodeData] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const init = useCallback(()=>{
    api.getPublicSettings().then(settings => {
      setInviteOnly(settings.inviteOnly);
    });
    if (code) {
      setUseInviteCode(true);
      setInviteCode(code);
      checkInvite(code);
    }
  },[code])
  const checkInvite = (code) => {
    if(code.length===10){
      api.checkInviteCode(code).then((inviteData)=>{
        setInviteCodeData(inviteData)
      }).catch((err)=>{
        setInviteCodeData(null)
      })
    }else{
      setInviteCodeData(null);
    }
  }
  useEffect(() => {
    init();
    if (session.isAuthenticated()) {
      navigate("/app");
    }
  }, [init, navigate]);

  const onSubmit = (event) => {
    event.preventDefault();
    setIsLoading(true);
    setError(null);

    // Client-side validation
    if (password !== cPassword) {
      setError("Passwords do not match");
      setIsLoading(false);
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      setIsLoading(false);
      return;
    }

    api
      .signUp({ email, username, password, cpassword: cPassword, inviteCode })
      .then((resp) => {
        trackEvent('Sign Up', { username, hasInviteCode: !!inviteCode });
        session.setSession(resp.access_token);
        navigate(navigator.userAgent.includes("Mobile") ? "/" : "/app");
      })
      .catch((err) => {
        setIsLoading(false);
        if (err.message) {
          setError(err.message);
        } else {
          setError("Could not communicate with the server properly.");
        }
      });
  };

  // Calculate password strength
  const getPasswordStrength = () => {
    if (!password) return { score: 0, label: "", color: "" };
    let score = 0;
    if (password.length >= 6) score++;
    if (password.length >= 10) score++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[^a-zA-Z\d]/.test(password)) score++;

    if (score <= 1) return { score, label: "Weak", color: "bg-red-500" };
    if (score <= 3) return { score, label: "Fair", color: "bg-yellow-500" };
    return { score, label: "Strong", color: "bg-green-500" };
  };

  const passwordStrength = getPasswordStrength();

  return (
    <div className="flex flex-col items-center justify-center min-h-screen w-full bg-primary p-4 overflow-y-auto">
      <div className="w-full max-w-md my-8 flex flex-col items-center">
        {/* Back to Home Button */}
        <Link
          to="/"
          className="self-start mb-4 flex items-center text-gray-400 hover:text-white transition-colors group"
        >
          <svg className="w-5 h-5 mr-2 transform group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Home
        </Link>

        {/* Card Container */}
        <div className="w-full bg-secondary border border-neutral-800 rounded-md shadow-2xl p-8 md:p-10 animate-scale-in">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img
              src={logo}
              alt="Muzio Logo"
              className="w-20 h-20 md:w-24 md:h-24 hover:scale-105 transition-transform"
            />
          </div>

          {/* Title */}
          <h1 className="text-2xl md:text-3xl font-bold text-white text-center mb-2">
            Join Muzio
          </h1>
          <p className="text-gray-400 text-center mb-6">
            Create your account to start sharing music
          </p>

          {/* Form */}
          <form onSubmit={onSubmit} className="space-y-4">
            {/* Error Message */}
            {error && (
              <div className="bg-accent bg-opacity-20 border border-accent text-white px-4 py-3 rounded-lg animate-slide-down">
                <p className="text-sm text-center">{error}</p>
              </div>
            )}

            {/* Email Input */}
            <div className="space-y-2">
              <label htmlFor="email" className="block text-sm font-medium text-gray-300">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                name="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                className="w-full px-4 py-3 bg-neutral-800 border border-neutral-700 rounded-md text-white placeholder-text-dim focus:outline-none focus:border-accent transition-colors"
              />
            </div>

            {/* Username Input */}
            <div className="space-y-2">
              <label htmlFor="username" className="block text-sm font-medium text-gray-300">
                Username
              </label>
              <input
                id="username"
                type="text"
                name="username"
                placeholder="Choose a username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="w-full px-4 py-3 bg-neutral-800 border border-neutral-700 rounded-md text-white placeholder-text-dim focus:outline-none focus:border-accent transition-colors"
              />
            </div>

            {/* Password Input */}
            <div className="space-y-2">
              <label htmlFor="password" className="block text-sm font-medium text-gray-300">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Create a password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full px-4 py-3 bg-neutral-800 border border-neutral-700 rounded-md text-white placeholder-text-dim focus:outline-none focus:border-accent transition-colors pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {showPassword ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    ) : (
                      <>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </>
                    )}
                  </svg>
                </button>
              </div>
              {/* Password Strength Indicator */}
              {password && (
                <div className="mt-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-400">Password Strength:</span>
                    <span className={`text-xs font-medium ${passwordStrength.color.replace('bg-', 'text-')}`}>
                      {passwordStrength.label}
                    </span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${passwordStrength.color}`}
                      style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Confirm Password Input */}
            <div className="space-y-2">
              <label htmlFor="cpassword" className="block text-sm font-medium text-gray-300">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  id="cpassword"
                  type={showConfirmPassword ? "text" : "password"}
                  name="cpassword"
                  placeholder="Confirm your password"
                  value={cPassword}
                  onChange={(e) => setCPassword(e.target.value)}
                  required
                  className="w-full px-4 py-3 bg-neutral-800 border border-neutral-700 rounded-md text-white placeholder-text-dim focus:outline-none focus:border-accent transition-colors pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {showConfirmPassword ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    ) : (
                      <>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </>
                    )}
                  </svg>
                </button>
              </div>
            </div>

            {/* Invite Code Toggle (if not invite-only) */}
            {!inviteOnly && (
              <button
                type="button"
                onClick={() => {
                  if (useInviteCode) {
                    setInviteCode(null);
                  } else {
                    setInviteCode(code);
                  }
                  setUseInviteCode(!useInviteCode);
                }}
                className="w-full text-accent hover:text-accent text-sm font-medium transition-colors py-2"
              >
                {useInviteCode ? "Don't use invite code" : "I have an invite code!"}
              </button>
            )}

            {/* Invite Code Input */}
            {(inviteOnly || useInviteCode) && (
              <div className="space-y-2">
                <label htmlFor="inviteCode" className="block text-sm font-medium text-gray-300">
                  Invite Code {inviteOnly && <span className="text-accent">*</span>}
                </label>
                <input
                  id="inviteCode"
                  type="text"
                  name="inviteCode"
                  placeholder="Enter your invite code"
                  value={inviteCode || ""}
                  onChange={(e) => {
                    const code = e.target.value;
                    checkInvite(code);
                    setInviteCode(code);
                  }}
                  required
                  className="w-full px-4 py-3 bg-neutral-800 border border-neutral-700 rounded-md text-white placeholder-text-dim focus:outline-none focus:border-accent transition-colors"
                />
                {/* Invite Code Validation */}
                {inviteCode && inviteCode.length === 10 && (
                  <div className="mt-2">
                    {inviteCodeData ? (
                      <p className="text-green-500 text-sm flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        Invited by {inviteCodeData.username}
                      </p>
                    ) : (
                      <p className="text-red-500 text-sm flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                        Invalid invitation code
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isLoading}
              variant="primary"
              size="lg"
              className="w-full"
            >
              {isLoading ? "Creating Account..." : "Create Account"}
            </Button>
          </form>

          {/* Login Link */}
          <p className="text-center text-gray-400 mt-6">
            Already have an account?{" "}
            <Link
              to="/auth"
              className="text-accent hover:text-accent font-medium transition-colors"
            >
              Sign in here
            </Link>
          </p>
        </div>

        {/* Footer */}
        <p className="text-center text-gray-500 text-sm mt-6">
          © 2024 Muzio. All rights reserved.
        </p>
      </div>
    </div>
  );
}
